/*
  Meeka storyline — scroll orchestration
  ===========================================================================
  GSAP + ScrollTrigger, both vendored into assets/ rather than pulled from a
  CDN, so `shopify theme check` stays clean of RemoteAsset offenses and the
  page has no third-party runtime dependency.

  Principles this file sticks to:

  - Content is authored visible in the HTML. This script adds `.ms-js` to opt
    into the hidden start state, so a failed script or a slow connection leaves
    a complete, readable page rather than a blank one.
  - `prefers-reduced-motion` is honoured by not building the timelines at all —
    not by playing them faster.
  - Desktop pins, mobile doesn't. Pinning a phone through four story beats is
    how you get someone to close the tab. matchMedia handles the split and
    cleans up after itself on resize.
  - Transform and opacity only. Nothing here animates layout.
*/
(function () {
  'use strict';

  /*
    The overture section owns the <script> tags for this page. The guard is
    belt-and-braces: if a future template loads this file twice, the second
    execution must not build a second set of timelines over the same elements.
  */
  if (window.__meekaStoryLoaded) return;
  window.__meekaStoryLoaded = true;

  /*
    The hero fills the viewport below everything stacked above it.

    `--header-height` alone is not enough: it measures the header only, while
    the announcement bar sits above that and is part of the same header group
    (41px of it on this store). Measuring the section's own document offset
    catches the header, the announcement bar, and anything added above it later
    without this needing to know what any of them are.

    This deliberately sits OUTSIDE the reduced-motion and GSAP-missing guards
    below — it is layout, not motion, and must apply to every visitor.
  */
  function setHeroOffset() {
    document.querySelectorAll('[data-ms-overture]').forEach(function (el) {
      var offset = Math.round(el.getBoundingClientRect().top + window.scrollY);
      el.style.setProperty('--ms-hero-offset', offset + 'px');
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-meeka-story]');
    if (!roots.length) return;

    setHeroOffset();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        setHeroOffset();
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }, 150);
    });

    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
      // Libraries missing: leave the page in its authored, visible state.
      return;
    }

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    roots.forEach(function (root) {
      root.classList.add('ms-js');
    });

    var EASE = 'power3.out';

    /* ---------------------------------------------------------------------
       Overture — one orchestrated load, then the title yields to the scroll.
       --------------------------------------------------------------------- */
    document.querySelectorAll('[data-ms-overture]').forEach(function (el) {
      var bits = el.querySelectorAll('.ms-anim');

      gsap.to(bits, {
        opacity: 1,
        y: 0,
        duration: 1.1,
        ease: EASE,
        stagger: 0.12,
        delay: 0.15
      });

      gsap.from(bits, { y: 28, duration: 1.1, ease: EASE, stagger: 0.12, delay: 0.15 });

      var orb = el.querySelector('.meeka-story__orb');
      if (orb) {
        gsap.fromTo(
          orb,
          { scale: 0.82, opacity: 0 },
          { scale: 1, opacity: 0.75, duration: 1.8, ease: EASE, delay: 0.1 }
        );
        gsap.to(orb, {
          yPercent: 22,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: 0.6 }
        });
      }

      // The title drifts up and dims as the first chapter arrives.
      var inner = el.querySelector('.ms-overture__inner');
      if (inner) {
        gsap.to(inner, {
          yPercent: -14,
          opacity: 0.15,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: 0.5 }
        });
      }

      var cue = el.querySelector('.ms-overture__cue-line');
      if (cue) {
        gsap.fromTo(
          cue,
          { scaleY: 0.25, opacity: 0.3 },
          {
            scaleY: 1,
            opacity: 1,
            duration: 1.6,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true
          }
        );
      }
    });

    /* ---------------------------------------------------------------------
       Chapters — crossfaded beats over a pinned stage (desktop only).
       --------------------------------------------------------------------- */
    document.querySelectorAll('[data-ms-chapters]').forEach(function (el) {
      var panels = Array.prototype.slice.call(el.querySelectorAll('.ms-chapter'));
      var plates = Array.prototype.slice.call(el.querySelectorAll('.ms-chapters__plates .ms-plate'));
      var railItems = Array.prototype.slice.call(el.querySelectorAll('.ms-chapters__rail-item'));
      if (!panels.length) return;

      ScrollTrigger.matchMedia({
        /* ---- Desktop: pin and crossfade ---- */
        '(min-width: 990px)': function () {
          var stage = el.querySelector('.ms-chapters__stage');
          if (!stage) return;

          /*
            On desktop the PANEL is the thing that fades, so the .ms-anim bits
            inside it must be released from their hidden start state — otherwise
            the panel fades in around permanently invisible text.
          */
          gsap.set(el.querySelectorAll('.ms-chapter .ms-anim'), { opacity: 1, y: 0 });

          gsap.set(panels, { opacity: 0, y: 26 });
          gsap.set(panels[0], { opacity: 1, y: 0 });
          if (plates.length) {
            gsap.set(plates, { opacity: 0 });
            gsap.set(plates[0], { opacity: 1 });
          }
          if (railItems.length) railItems[0].classList.add('is-active');

          var tl = gsap.timeline({
            scrollTrigger: {
              trigger: el,
              start: 'top top',
              // One viewport of scroll per chapter after the first.
              end: function () {
                return '+=' + window.innerHeight * (panels.length - 1) * 1.15;
              },
              pin: stage,
              pinSpacing: true,
              scrub: 0.7,
              invalidateOnRefresh: true
            }
          });

          panels.forEach(function (panel, i) {
            if (i === 0) return;
            var prev = panels[i - 1];

            /*
              Overlap is kept short on purpose. A long crossfade leaves two
              chapters legible at once, which reads as a rendering fault rather
              than a transition — the outgoing beat is most of the way gone
              before the incoming one starts.
            */
            tl.to(prev, { opacity: 0, y: -26, duration: 0.45, ease: 'power2.in' }, '+=0.5')
              .to(panel, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '<0.32');

            if (plates[i]) {
              tl.to(plates[i - 1], { opacity: 0, duration: 0.5, ease: 'power2.inOut' }, '<-0.2')
                .to(plates[i], { opacity: 1, duration: 0.6, ease: 'power2.out' }, '<0.1');
            }

          });

          /*
            The numeral rail tracks whichever beat is actually most visible,
            recomputed on every tick. Timeline .call() callbacks were the
            obvious approach and the wrong one: scrubbing backwards fires them
            in reverse order too, so the rail ended up pointing at the beat the
            reader had just left.
          */
          if (railItems.length) {
            tl.eventCallback('onUpdate', function () {
              var best = 0;
              var bestOpacity = -1;
              panels.forEach(function (panel, k) {
                var o = Number(gsap.getProperty(panel, 'opacity'));
                if (o > bestOpacity) {
                  bestOpacity = o;
                  best = k;
                }
              });
              railItems.forEach(function (item, k) {
                item.classList.toggle('is-active', k === best);
              });
            });
          }
        },

        /* ---- Mobile: no pinning, just reveals ---- */
        '(max-width: 989px)': function () {
          gsap.set(panels, { opacity: 1, y: 0 });
          panels.forEach(function (panel) {
            var bits = panel.querySelectorAll('.ms-anim');
            if (!bits.length) return;
            gsap.to(bits, {
              opacity: 1,
              y: 0,
              duration: 0.9,
              ease: EASE,
              stagger: 0.1,
              scrollTrigger: { trigger: panel, start: 'top 82%', once: true }
            });
            gsap.from(bits, {
              y: 22,
              duration: 0.9,
              ease: EASE,
              stagger: 0.1,
              scrollTrigger: { trigger: panel, start: 'top 82%', once: true }
            });
          });
        }
      });
    });

    /* ---------------------------------------------------------------------
       Generic reveals — values, CTA, anything else carrying .ms-anim.
       --------------------------------------------------------------------- */
    document.querySelectorAll('[data-ms-reveal]').forEach(function (el) {
      var bits = el.querySelectorAll('.ms-anim');
      if (!bits.length) return;

      gsap.to(bits, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: EASE,
        stagger: 0.11,
        scrollTrigger: { trigger: el, start: 'top 78%', once: true }
      });
      gsap.from(bits, {
        y: 26,
        duration: 1,
        ease: EASE,
        stagger: 0.11,
        scrollTrigger: { trigger: el, start: 'top 78%', once: true }
      });
    });

    /* ---------------------------------------------------------------------
       Parallax on arch imagery. Subtle — 8% of travel, no more.
       --------------------------------------------------------------------- */
    document.querySelectorAll('[data-ms-parallax] img').forEach(function (img) {
      gsap.fromTo(
        img,
        { yPercent: -4 },
        {
          yPercent: 4,
          ease: 'none',
          scrollTrigger: { trigger: img.closest('.ms-plate') || img, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
        }
      );
    });

    /* Sections can be added or removed in the theme editor while previewing. */
    document.addEventListener('shopify:section:load', function () {
      ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:unload', function () {
      ScrollTrigger.refresh();
    });

    // Web fonts land after first paint and change every measurement.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        setHeroOffset();
        ScrollTrigger.refresh();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
